/**
 * Profile identity block (specs/profile-page AC-5..8, AC-11, AC-12):
 * name + tier label + level, a big tappable avatar (opens the layered lab) with the rank
 * crest overlapping its corner, and the XP progress bar.
 */

import React from 'react';
import { View, Text, Pressable, Platform, type ImageSourcePropType } from 'react-native';
import { Image } from 'expo-image';
import { AvatarDisplay } from '../../../../components/avatars/AvatarDisplay/AvatarDisplay';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../shared/helpers/imageWebAlt.helper';
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import type { ProfileXpDisplay } from '../../helpers/profileStats.helper';
import { PROFILE_LAYOUT, PROFILE_TEST_IDS } from '../ProfileScreen/ProfileScreen.constants';
import { profileIdentityStyles as styles } from './ProfileIdentity.styles';

export interface ProfileIdentityProps {
  displayName: string;
  rankName: string;
  tierColor: string;
  rankBadge: ImageSourcePropType;
  xp: ProfileXpDisplay | null;
  avatarId: DisplayAvatarId | null;
  avatarConfig: EquippedMap | null;
  onAvatarPress: () => void;
  testID?: string;
}

export const ProfileIdentity: React.FC<ProfileIdentityProps> = ({
  displayName,
  rankName,
  tierColor,
  rankBadge,
  xp,
  avatarId,
  avatarConfig,
  onAvatarPress,
  testID,
}) => (
  <View testID={testID}>
    {/* name + tier + level */}
    <View style={styles.nameBlock}>
      <Text style={styles.name} numberOfLines={1}>{displayName}</Text>
      <View style={styles.tierRow}>
        <Text style={[styles.tierLabel, { color: tierColor }]}>{rankName.toUpperCase()}</Text>
        {xp ? (
          <>
            <Text style={styles.tierDot}>·</Text>
            <Text style={styles.levelText}>Level {xp.level}</Text>
          </>
        ) : null}
      </View>
    </View>

    {/* big tappable avatar + corner crest */}
    <View style={styles.heroWrap}>
      <Pressable
        onPress={onAvatarPress}
        accessibilityRole="button"
        accessibilityLabel="Customize your avatar"
        testID={PROFILE_TEST_IDS.avatar}
        style={({ pressed, hovered }) => [
          styles.avatarPress,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.avatarPressed,
          hovered && Platform.OS === 'web' && styles.avatarHover,
        ]}
      >
        <AvatarDisplay
          avatarId={avatarId ?? undefined}
          avatarConfig={avatarConfig}
          size={PROFILE_LAYOUT.avatarSize}
          rounded={false}
        />
      </Pressable>
      <Image
        source={rankBadge}
        style={styles.crest}
        contentFit="contain"
        alt={WEB_IMAGE_ALT_DECORATIVE}
        testID={PROFILE_TEST_IDS.crest}
      />
    </View>

    {/* XP bar */}
    {xp ? (
      <View style={styles.xpBlock} testID={PROFILE_TEST_IDS.xpBar}>
        <View style={styles.xpLabelRow}>
          <Text style={styles.xpLevelLabel}>LEVEL {xp.level}</Text>
          <Text style={styles.xpToGo}>
            <Text style={styles.xpToGoStrong}>{xp.xpToGo.toLocaleString()} XP</Text>
            {` to Level ${xp.levelNext}`}
          </Text>
        </View>
        <View style={styles.xpTrack}>
          <View style={[styles.xpFill, { width: `${Math.round(xp.progress * 100)}%` }]} />
        </View>
      </View>
    ) : null}
  </View>
);
